// src/components/ProfilePage.jsx
// User profile, subscription tier, upgrade/cancel, sign out
import React, { useState } from 'react'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import { PLANS, getTierLabel, getTierColor } from '../lib/stripe'

export default function ProfilePage({ onUpgrade }) {
  const { user, tier } = useAuth()
  const [portalLoading, setPortalLoading] = useState(false)
  const [portalError,   setPortalError]   = useState('')
  const [signOutLoading, setSignOutLoading] = useState(false)

  const tierLabel = getTierLabel(tier)
  const tierColor = getTierColor(tier)
  const currentPlan = PLANS.find(p => p.id === tier) || PLANS[0]

  // ─── Open Stripe Customer Portal ──────────────────────────
  async function handleManageSubscription() {
    setPortalLoading(true)
    setPortalError('')
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/customer-portal`,
        {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ userId: user.id }),
        }
      )
      const { url, error: fnError } = await res.json()
      if (fnError) throw new Error(fnError)
      window.location.href = url
    } catch (err) {
      setPortalError('Could not open billing portal. Try again.')
    }
    setPortalLoading(false)
  }

  // ─── Sign Out ──────────────────────────────────────────────
  async function handleSignOut() {
    setSignOutLoading(true)
    await supabase.auth.signOut()
    setSignOutLoading(false)
  }

  if (!user) {
    return (
      <div style={s.empty}>
        <div style={s.emptyIcon}>◉</div>
        <div style={s.emptyTitle}>Not signed in</div>
        <div style={s.emptySub}>Sign in to view your profile and subscription</div>
      </div>
    )
  }

  return (
    <div style={s.page}>

      {/* ── User Card ── */}
      <div style={s.userCard}>
        <div style={s.avatar}>
          {user.email?.[0]?.toUpperCase() || 'U'}
        </div>
        <div style={s.userInfo}>
          <div style={s.userEmail}>{user.email}</div>
          <div style={s.userId}>ID: {user.id.slice(0, 8)}...</div>
        </div>
      </div>

      {/* ── Subscription Tier ── */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Current Plan</div>
        <div style={{ ...s.tierCard, borderColor: tierColor + '44' }}>
          <div style={s.tierTop}>
            <div style={s.tierEmoji}>{currentPlan.emoji}</div>
            <div>
              <div style={{ ...s.tierName, color: tierColor }}>{tierLabel}</div>
              <div style={s.tierDesc}>{currentPlan.description}</div>
            </div>
            <div style={{ ...s.tierBadge, background: tierColor + '18', color: tierColor, borderColor: tierColor + '44' }}>
              {tier === 'free' ? 'Active' : 'Subscribed'}
            </div>
          </div>

          {/* Feature List */}
          <div style={s.featureList}>
            {currentPlan.features?.map((f, i) => (
              <div key={i} style={{ ...s.featureRow, color: f.included ? 'var(--text-secondary, #aaa)' : '#444' }}>
                <span style={{ color: f.included ? '#4caf7d' : '#444', fontSize: 12 }}>
                  {f.included ? '✓' : '✕'}
                </span>
                {f.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Subscription Actions ── */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Subscription</div>

        {tier === 'free' ? (
          <div style={s.upgradeSection}>
            <div style={s.upgradeText}>
              Upgrade to unlock terminal performance ratings, real-time GPS weather, ethical range calculator, and unlimited rifle profiles.
            </div>
            <button style={s.upgradeBtn} onClick={onUpgrade}>
              ⚡ Upgrade to Pro — $34.99/yr →
            </button>
            <button style={{ ...s.upgradeBtn, ...s.eliteBtn }} onClick={onUpgrade}>
              💎 Upgrade to Elite — $69.99/yr →
            </button>
          </div>
        ) : (
          <div style={s.manageSection}>
            <div style={s.manageInfo}>
              <div style={s.manageInfoRow}>
                <span style={s.manageInfoLabel}>Plan</span>
                <span style={{ color: tierColor, fontFamily: 'Courier New, monospace', fontSize: 13 }}>{tierLabel}</span>
              </div>
              <div style={s.manageInfoRow}>
                <span style={s.manageInfoLabel}>Billing</span>
                <span style={{ color: '#aaa', fontFamily: 'Courier New, monospace', fontSize: 13 }}>Managed via Stripe</span>
              </div>
            </div>

            {portalError && (
              <div style={s.errorMsg}>{portalError}</div>
            )}

            <button
              style={{ ...s.manageBtn, opacity: portalLoading ? 0.7 : 1 }}
              onClick={handleManageSubscription}
              disabled={portalLoading}
            >
              {portalLoading ? 'Opening billing portal...' : 'Manage Subscription →'}
            </button>
            <div style={s.manageNote}>
              Update payment method, change plan, or cancel — handled securely by Stripe.
            </div>

            <button
              style={s.cancelBtn}
              onClick={handleManageSubscription}
              disabled={portalLoading}
            >
              Cancel Subscription
            </button>
          </div>
        )}
      </div>

      {/* ── Live Pricing Note ── */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Affiliate Pricing</div>
        <div style={s.infoCard}>
          <div style={s.infoIcon}>💰</div>
          <div>
            <div style={s.infoTitle}>Live Ammo Pricing — Always Free</div>
            <div style={s.infoDesc}>
              Live pricing data and affiliate buy links are available on all tiers. No paywall on pricing information — ever.
            </div>
          </div>
        </div>
      </div>

      {/* ── Account Actions ── */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Account</div>
        <div style={s.accountActions}>
          <button
            style={{ ...s.signOutBtn, opacity: signOutLoading ? 0.7 : 1 }}
            onClick={handleSignOut}
            disabled={signOutLoading}
          >
            {signOutLoading ? 'Signing out...' : 'Sign Out'}
          </button>
        </div>
        <div style={s.accountNote}>
          DISDownrange is a product of Dynamic Innovative Solutions LLC
        </div>
      </div>

    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────
const s = {
  page:  { display: 'flex', flexDirection: 'column', gap: 0, paddingBottom: 40 },

  empty:      { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 30px', gap: 14 },
  emptyIcon:  { fontSize: 40, color: 'var(--accent, #c8a96e)' },
  emptyTitle: { fontSize: 16, fontWeight: 600, color: '#aaa' },
  emptySub:   { fontSize: 12, color: '#666', fontFamily: 'Courier New, monospace', textAlign: 'center' },

  userCard: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '20px', background: '#111',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  avatar: {
    width: 50, height: 50, borderRadius: '50%', flexShrink: 0,
    background: 'rgba(200,169,110,0.15)', border: '2px solid rgba(200,169,110,0.3)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 20, fontWeight: 700, color: '#c8a96e',
  },
  userInfo:  { flex: 1, overflow: 'hidden' },
  userEmail: { fontSize: 14, color: '#f0ede8', fontFamily: 'Courier New, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userId:    { fontSize: 10, color: '#555', fontFamily: 'Courier New, monospace', marginTop: 4 },

  section:      { padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  sectionLabel: { fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'Courier New, monospace', marginBottom: 12 },

  tierCard: {
    background: '#141414', border: '1px solid',
    borderRadius: 12, padding: 16,
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  tierTop:   { display: 'flex', alignItems: 'flex-start', gap: 12 },
  tierEmoji: { fontSize: 24, flexShrink: 0 },
  tierName:  { fontSize: 16, fontWeight: 700, fontFamily: 'Georgia, serif' },
  tierDesc:  { fontSize: 11, color: '#666', fontFamily: 'Courier New, monospace', marginTop: 3, lineHeight: 1.4 },
  tierBadge: { marginLeft: 'auto', fontSize: 10, padding: '3px 10px', borderRadius: 4, border: '1px solid', fontFamily: 'Courier New, monospace', letterSpacing: '0.06em', flexShrink: 0, textTransform: 'uppercase' },
  featureList: { display: 'flex', flexDirection: 'column', gap: 7, borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 12 },
  featureRow:  { display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, fontFamily: 'Courier New, monospace' },

  upgradeSection: { display: 'flex', flexDirection: 'column', gap: 10 },
  upgradeText:    { fontSize: 12, color: '#888', fontFamily: 'Courier New, monospace', lineHeight: 1.6, marginBottom: 4 },
  upgradeBtn: {
    background: '#c8a96e', color: '#0a0a0a', border: 'none',
    borderRadius: 9, padding: '13px 16px', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '0.02em',
    textAlign: 'left', transition: 'opacity 0.2s',
  },
  eliteBtn: {
    background: 'transparent', color: '#c8a96e',
    border: '1px solid rgba(200,169,110,0.3)',
  },

  manageSection:  { display: 'flex', flexDirection: 'column', gap: 10 },
  manageInfo:     { background: '#141414', borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 },
  manageInfoRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  manageInfoLabel:{ fontSize: 11, color: '#666', fontFamily: 'Courier New, monospace' },
  manageBtn: {
    background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 9, padding: '12px 16px', fontSize: 13, color: '#aaa',
    cursor: 'pointer', fontFamily: 'Courier New, monospace', textAlign: 'left',
    transition: 'all 0.2s',
  },
  manageNote: { fontSize: 10, color: '#444', fontFamily: 'Courier New, monospace', lineHeight: 1.5 },
  cancelBtn: {
    background: 'transparent', border: '1px solid rgba(224,92,92,0.2)',
    borderRadius: 9, padding: '10px 16px', fontSize: 12, color: '#e05c5c',
    cursor: 'pointer', fontFamily: 'Courier New, monospace', textAlign: 'left',
  },

  errorMsg: { fontSize: 11, color: '#e05c5c', fontFamily: 'Courier New, monospace', padding: '8px 12px', background: 'rgba(224,92,92,0.08)', borderRadius: 6 },

  infoCard:  { display: 'flex', alignItems: 'flex-start', gap: 12, background: '#141414', borderRadius: 10, padding: '14px', border: '1px solid rgba(76,175,125,0.15)' },
  infoIcon:  { fontSize: 22, flexShrink: 0 },
  infoTitle: { fontSize: 13, fontWeight: 600, color: '#4caf7d', marginBottom: 4, fontFamily: 'Georgia, serif' },
  infoDesc:  { fontSize: 11, color: '#888', fontFamily: 'Courier New, monospace', lineHeight: 1.5 },

  accountActions: { display: 'flex', flexDirection: 'column', gap: 10 },
  signOutBtn: {
    background: 'transparent', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 9, padding: '12px 16px', fontSize: 13, color: '#666',
    cursor: 'pointer', fontFamily: 'Courier New, monospace', textAlign: 'left',
    transition: 'all 0.2s',
  },
  accountNote: { fontSize: 10, color: '#333', fontFamily: 'Courier New, monospace', marginTop: 12, lineHeight: 1.5 },
}
